import { RefObject, useEffect, useState } from 'react';
import debounce from 'lodash/debounce';

interface CarouselScrollParams {
  carouselRef: RefObject<HTMLElement>;
  templatesRefs: RefObject<HTMLElement>[];
}

export const useCarouselScroll = ({
  carouselRef,
  templatesRefs,
}: CarouselScrollParams) => {
  const [state, setState] = useState({
    isStartPosition: true,
    isEndPosition: false,
  });

  useEffect(() => {
    const handleScroll = debounce(
      () => {
        if (carouselRef.current) {
          const isStartPosition = carouselRef.current.scrollLeft === 0;
          const isEndPosition =
            carouselRef.current.scrollLeft ===
            carouselRef.current.scrollWidth - carouselRef.current.clientWidth;

          setState({ isStartPosition, isEndPosition });
        }
      },
      100,
      { leading: true },
    );

    carouselRef.current?.addEventListener('scroll', handleScroll);
    return () => {
      carouselRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTemplate = (templatesIndex: number) => {
    templatesRefs[templatesIndex].current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  };

  const calculateTemplatesPositions = () => {
    const parentRect = carouselRef.current?.getBoundingClientRect();

    return templatesRefs.map((templateRef) => {
      const templateRect = templateRef.current?.getBoundingClientRect();
      if (templateRect && parentRect) {
        return templateRect.left - parentRect.left;
      }
      return 0;
    });
  };

  const getPreviousTemplateIndex = () => {
    const templatesPositions = calculateTemplatesPositions();
    const index = templatesPositions.findIndex((position) => position >= 0);

    if (index === -1) {
      return templatesPositions.length - 1;
    }

    return index - 1;
  };

  const getNextTemplateIndex = () => {
    const templatesPositions = calculateTemplatesPositions();
    return templatesPositions.findIndex((position) => position > 0);
  };

  const showNextTemplate = () => {
    const nextTemplateIndex = getNextTemplateIndex();
    if (~nextTemplateIndex) {
      scrollToTemplate(nextTemplateIndex);
    }
  };

  const showPreviousTemplate = () => {
    const previousTemplateIndex = getPreviousTemplateIndex();
    if (~previousTemplateIndex) {
      scrollToTemplate(previousTemplateIndex);
    }
  };

  return {
    showNextTemplate,
    showPreviousTemplate,
    ...state,
  };
};
